/**
 * E-GTCC-VIEW-TEN1, tab "Einladungen" — the reference draws this as its own
 * tab, separate from "Mitglieder" and "Rollen" (`GTCC_REFERENCE_2026-09-01.html:682`).
 * B-GTCC-FIX-TEN1 splits it out of the combined "Mitglieder"/"Einladungen +
 * Rollen" tabs that E-GTCC-VIEW-TEN1 shipped as a deliberate Owner-order
 * deviation (`ABWEICHUNGEN.md` §E "ten", point 1).
 *
 * The rows are the reference's `.row`s; every write behind them is the gtcc1
 * card, reused unchanged — invitation creation and the one-time link live in
 * `InvitationsSection` (`GtccTenantsView.tsx`).
 */
import { EmptyState, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { InvitationsSection } from "../GtccTenantsView.js";
import { fetchTenInvitations } from "../../../api/ten.js";
import {
  TEN_QUERY_OPTIONS,
  TenGate,
  TenMutedNote,
  TenPanel,
  TenRow,
  type TenTabProps,
} from "../components/TenPanels.js";

export function InvitationsTab(props: TenTabProps): ReactNode {
  const { t } = useLocale();
  const tenantId = props.selectedTenantId;
  const enabled = tenantId !== null;
  const id = tenantId ?? "";

  const invitations = useQuery({
    queryKey: ["gtcc", "ten", "view", "invitations", id],
    queryFn: ({ signal }) => fetchTenInvitations(id, signal),
    enabled,
    ...TEN_QUERY_OPTIONS,
  });

  if (tenantId === null) {
    return (
      <div data-testid="gtcc-ten-invitations-no-tenant">
        <EmptyState
          title={t("admin.gtcc.ten.t360.no_tenant_title")}
          body={t("admin.gtcc.ten.t360.no_tenant_body")}
        />
      </div>
    );
  }

  return (
    <TenPanel
      testId="gtcc-ten-invitations"
      title={t("admin.gtcc.ten.members_tab.invitations_panel")}
    >
      <Stack gap={2}>
        <TenGate
          query={invitations}
          testId="gtcc-ten-invitations-gate"
          render={(rows) =>
            rows.filter((row) => row.status === "pending").length === 0 ? (
              <TenMutedNote testId="gtcc-ten-invitations-none">
                {t("admin.gtcc.ten.members_tab.invitations_none")}
              </TenMutedNote>
            ) : (
              <Stack gap={0}>
                {rows
                  .filter((row) => row.status === "pending")
                  .map((row) => (
                    <TenRow
                      key={row.id}
                      testId={`gtcc-ten-invitation-${row.id}`}
                      tone="warn"
                      label={row.email ?? row.id}
                      value={row.role ?? "—"}
                    />
                  ))}
              </Stack>
            )
          }
        />
        <InvitationsSection tenantId={tenantId} />
      </Stack>
    </TenPanel>
  );
}
